import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import ActiveScanIndicator from './ActiveScanIndicator';
import LicenseBanner from '../shared/LicenseBanner';
import useActiveClassificationScan from '../../hooks/useActiveClassificationScan';

export default function AppLayout() {
  // App-shell-level poll. Mounted here (not inside ClassificationPage) so
  // the scan keeps being tracked when the user navigates between modules.
  // Polling stops automatically when the scan finishes.
  useActiveClassificationScan();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1280) {
        setSidebarCollapsed(false);
        setSidebarMobileOpen(false);
      } else if (width >= 1024) {
        setSidebarCollapsed(true);
        setSidebarMobileOpen(false);
      } else {
        setSidebarMobileOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // CSS variable consumed by fixed full-width bars (e.g. BulkActionBar) so
  // they can offset from the sidebar without needing sidebar state as props.
  const sidebarWidth = window.innerWidth >= 1024
    ? (sidebarCollapsed ? '5rem' : '16rem')
    : '0px';

  return (
    // overflow-x-clip: the mobile drawer (Sidebar.js) is `position: fixed` and,
    // when closed, translates off the inline-start edge. In RTL that edge is the
    // right, so the off-canvas drawer's content pokes ~6px past the viewport and
    // adds a document-level horizontal scrollbar on every route at ≤320px. `clip`
    // (never `hidden`) removes it WITHOUT creating a scroll container — per the
    // CSS overflow visible/clip coercion rule `overflow-x:clip` leaves
    // `overflow-y:visible`, so the desktop sticky sidebar and vertical page scroll
    // are untouched. LTR was already clean (drawer hides off the left, which never
    // scrolls); the clip is a symmetric, defensive shell-level guard.
    <div data-testid="app-shell" className="min-h-screen bg-gray-100 flex overflow-x-clip" style={{ '--sidebar-width': sidebarWidth }}>
      {/* Mobile overlay */}
      {sidebarMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarMobileOpen(false)}
        />
      )}

      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={sidebarMobileOpen}
        onMobileClose={() => setSidebarMobileOpen(false)}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onMenuToggle={() => setSidebarMobileOpen(true)} />
        {/* ON-13 — License state banner; renders null when license is VALID */}
        <LicenseBanner />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* Floating progress pill — visible from any page when a classification
          scan is in progress. Hidden on /classification (page has its own bar). */}
      <ActiveScanIndicator />
    </div>
  );
}
